// merged-away — turn a "this record was already merged" 404 into a path
// forward, so a stale Cleanup merge never dead-ends (Grant 2026-07-16).
//
// When a merge screen loads a record that has since been merged away,
// GET /api/contacts/:id and /api/households/:id return 404 with a body
// carrying { reason: "merged", merged_into_id, entity } (see
// shared/db/explain-missing-record.ts). This helper reads that off the
// ApiError so the error UI can offer an "Open the surviving record" link
// instead of just showing the message. The stale cleanup issue itself is
// now auto-resolved at merge time (resolveCleanupIssuesForArchivedRecord
// on the merge path), so the only affordance the user still needs here is
// a way to reach the survivor.
//
// Dependency-free on purpose (no import of api.ts) — the same convention
// session-expiry.ts follows — so it stays unit-testable under the
// node/root tsconfig without dragging api.ts's DOM-typed fetch in. It
// duck-types the thrown ApiError (a 404 carrying a JSON `data` body)
// rather than importing the class.

export interface MergedAwayInfo {
  entity: "contact" | "household";
  /** The surviving record the missing one was merged into. */
  survivorId: string;
  /** Server's plain-English explanation. */
  message: string;
  /** Route to the surviving record. */
  href: string;
}

export function mergedAwayFromError(err: unknown): MergedAwayInfo | null {
  // Duck-type the ApiError shape (status + parsed JSON `data` body)
  // instead of importing the class — keeps this module DOM-free.
  if (
    !err ||
    typeof err !== "object" ||
    (err as { status?: unknown }).status !== 404
  ) {
    return null;
  }
  const data = (err as { data?: unknown }).data as {
    entity?: unknown;
    reason?: unknown;
    merged_into_id?: unknown;
    message?: unknown;
  } | null;
  if (
    !data ||
    data.reason !== "merged" ||
    typeof data.merged_into_id !== "string" ||
    data.merged_into_id.length === 0
  ) {
    return null;
  }
  const entity =
    data.entity === "contact" || data.entity === "household"
      ? data.entity
      : null;
  if (!entity) return null;
  return {
    entity,
    survivorId: data.merged_into_id,
    message:
      typeof data.message === "string" && data.message.length > 0
        ? data.message
        : "This record was already merged into another record.",
    href: `/${entity === "contact" ? "contacts" : "households"}/${data.merged_into_id}`,
  };
}
